// Admin console: Subscriptions — every tenant's plan assignment, with a
// COHORT bulk-migrate flow (select a plan family's subscribers, preview the
// diff, then migrate them all to the plan's latest version in one action).
// Fixes "a routine pricing correction affecting dozens of tenants is
// unworkable one-by-one" (concept doc §4.5).
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { adminApi } from '../api/adminClient';
import type { MigrationPreviewTenant, SubscriptionStatus } from '../api/types';
import { useAsync } from '../lib/hooks';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { Badge, Button, Card } from '../components/ui';
import { useToast } from '../components/Toast';
import { IconClose, IconLayers } from '../components/Icons';

function statusTone(status: SubscriptionStatus): 'success' | 'warning' | 'default' {
  if (status === 'active') return 'success';
  return 'warning';
}

export function AdminSubscriptions() {
  const { t } = useTranslation();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const subs = useAsync(() => adminApi.adminSubscriptions(), []);
  const versions = useAsync(() => adminApi.adminAllPlanVersions(), []);
  const plans = useAsync(() => adminApi.adminPlans(), []);
  const tenants = useAsync(() => adminApi.adminTenants(), []);

  const versionInfo = useMemo(() => {
    const m = new Map<string, { planId: string; planName: string; version: number }>();
    for (const v of versions.data?.items ?? []) m.set(v.id, v);
    return m;
  }, [versions.data]);
  const tenantName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of tenants.data?.items ?? []) m.set(c.id, c.name);
    return m;
  }, [tenants.data]);
  const latestVersionByPlan = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of plans.data?.items ?? []) if (p.latestVersion) m.set(p.id, p.latestVersion.id);
    return m;
  }, [plans.data]);

  const rows = subs.data?.items ?? [];
  const selectedRows = rows.filter((r) => selected.has(r.tenantId));
  const selectedPlanIds = new Set(selectedRows.map((r) => versionInfo.get(r.planVersionId)?.planId).filter(Boolean));
  const canMigrate = selectedRows.length > 0 && selectedPlanIds.size === 1;
  const migrationPlanId = canMigrate ? [...selectedPlanIds][0]! : undefined;
  const migrationTargetVersionId = migrationPlanId ? latestVersionByPlan.get(migrationPlanId) : undefined;

  const toggle = (tenantId: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(tenantId)) next.delete(tenantId);
      else next.add(tenantId);
      return next;
    });

  const quickAction = async (tenantId: string, action: 'pause' | 'cancel' | 'reactivate') => {
    setBusy(true);
    try {
      await adminApi.adminSubscriptionsBulk([tenantId], action);
      toast.success(t('admin.subscriptions.actionDone'));
      subs.reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t('admin.subscriptions.title')}
        subtitle={t('admin.subscriptions.subtitle')}
        actions={
          <Button variant="primary" disabled={!canMigrate} onClick={() => setPreviewOpen(true)}>
            {t('admin.subscriptions.migrateSelected', { count: selectedRows.length })}
          </Button>
        }
      />

      <Card>
        <ApiState
          status={subs.status}
          error={subs.error}
          data={subs.status === 'error' ? null : rows}
          onRetry={subs.reload}
        >
          {(items) =>
            items.length === 0 ? (
              <EmptyState title={t('admin.subscriptions.empty')} icon={<IconLayers width={26} height={26} />} />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th aria-label={t('common.actions')} />
                      <th>{t('common.name')}</th>
                      <th>{t('admin.customers.colPlan')}</th>
                      <th>{t('admin.customers.colStatus')}</th>
                      <th className="num">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((s) => {
                      const info = versionInfo.get(s.planVersionId);
                      return (
                        <tr key={s.tenantId}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selected.has(s.tenantId)}
                              onChange={() => toggle(s.tenantId)}
                              aria-label={t('admin.subscriptions.selectRow', { name: tenantName.get(s.tenantId) ?? s.tenantId })}
                            />
                          </td>
                          <td className="cell-strong">
                            <Link to={`/customers/${encodeURIComponent(s.tenantId)}`}>
                              {tenantName.get(s.tenantId) ?? s.tenantId}
                            </Link>
                          </td>
                          <td>{info ? `${info.planName} (v${info.version})` : s.planVersionId}</td>
                          <td>
                            <Badge tone={statusTone(s.status)}>{t(`admin.status.${s.status}`)}</Badge>
                          </td>
                          <td className="num">
                            <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
                              {s.status !== 'paused' ? (
                                <Button variant="secondary" size="sm" disabled={busy} onClick={() => quickAction(s.tenantId, 'pause')}>
                                  {t('admin.status.actionPause')}
                                </Button>
                              ) : (
                                <Button variant="secondary" size="sm" disabled={busy} onClick={() => quickAction(s.tenantId, 'reactivate')}>
                                  {t('admin.status.actionReactivate')}
                                </Button>
                              )}
                              {s.status !== 'cancelled' ? (
                                <Button variant="danger" size="sm" disabled={busy} onClick={() => quickAction(s.tenantId, 'cancel')}>
                                  {t('admin.status.actionCancel')}
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          }
        </ApiState>
      </Card>

      {previewOpen && migrationPlanId && migrationTargetVersionId ? (
        <MigrationPreviewDialog
          planId={migrationPlanId}
          targetVersionId={migrationTargetVersionId}
          selectedTenantIds={[...selected]}
          tenantName={tenantName}
          onClose={() => setPreviewOpen(false)}
          onConfirmed={() => {
            setPreviewOpen(false);
            setSelected(new Set());
            toast.success(t('admin.subscriptions.migrated'));
            subs.reload();
          }}
        />
      ) : null}
    </>
  );
}

function MigrationPreviewDialog({
  planId,
  targetVersionId,
  selectedTenantIds,
  tenantName,
  onClose,
  onConfirmed,
}: {
  planId: string;
  targetVersionId: string;
  selectedTenantIds: string[];
  tenantName: Map<string, string>;
  onClose: () => void;
  onConfirmed: () => void;
}) {
  const { t } = useTranslation();
  const preview = useAsync(() => adminApi.adminMigrationPreview(planId, targetVersionId), [planId, targetVersionId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const selectedSet = new Set(selectedTenantIds);
  const relevant = (preview.data?.items ?? []).filter((it: MigrationPreviewTenant) => selectedSet.has(it.tenantId));

  const confirm = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await adminApi.adminSubscriptionsBulk(selectedTenantIds, 'migrate', targetVersionId);
      onConfirmed();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="migrate-title" style={{ maxWidth: 640 }}>
        <div className="row between">
          <h2 className="dialog__title" id="migrate-title">
            {t('admin.subscriptions.previewTitle', { count: selectedTenantIds.length })}
          </h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
            <IconClose width={16} height={16} />
          </button>
        </div>
        <div style={{ marginTop: 16, maxHeight: '55vh', overflowY: 'auto' }}>
          <ApiState status={preview.status} error={preview.error} data={preview.status === 'error' ? null : preview.data} onRetry={preview.reload}>
            {() =>
              relevant.length === 0 ? (
                <p>{t('admin.subscriptions.noChanges')}</p>
              ) : (
                relevant.map((item) => (
                  <div key={item.tenantId} style={{ marginBottom: 16 }}>
                    <h3 style={{ marginBottom: 6 }}>{tenantName.get(item.tenantId) ?? item.tenantId}</h3>
                    {item.droppedOverrides && item.droppedOverrides.length > 0 ? (
                      <p className="field__error">
                        {t('admin.subscriptions.droppedOverrides', { fields: item.droppedOverrides.join(', ') })}
                      </p>
                    ) : null}
                    {item.changes.length === 0 ? (
                      <p className="muted">{t('admin.subscriptions.noFieldChanges')}</p>
                    ) : (
                      <table className="data">
                        <thead>
                          <tr>
                            <th>{t('admin.customerDetail.colField')}</th>
                            <th>{t('admin.subscriptions.colOld')}</th>
                            <th>{t('admin.subscriptions.colNew')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.changes.map((c) => (
                            <tr key={c.field}>
                              <td>{t(`admin.fields.${c.field}`, c.field)}</td>
                              <td>{String(c.oldValue ?? '—')}</td>
                              <td>{String(c.newValue ?? '—')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))
              )
            }
          </ApiState>
        </div>
        {error ? <p className="field__error">{error}</p> : null}
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="primary" onClick={confirm} disabled={busy || preview.status !== 'success'}>
            {t('admin.subscriptions.confirmMigrate')}
          </Button>
        </div>
      </div>
    </div>
  );
}
